import { ConnectedAppointmentDetailModal } from '../appointments/components/ConnectedAppointmentDetailModal'
import type { AppointmentItem } from '../appointments/appointments-model'
import { Clock3, Info } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthProvider'
import {
  createReadSideRepository,
  readSidePresentation,
  type ReadSideRepository,
} from '../read-side/read-side-status'
import { useReadSideStatus } from '../read-side/use-read-side-status'

import { useTenant } from '../tenant/TenantProvider'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatusBadge, type StatusTone } from '../../ui/StatusBadge/StatusBadge'
import { NeedsAttention } from './components/NeedsAttention'
import {
  createBookingChecksRepository,
  type BookingChecksRepository,
} from '../booking-checks/booking-checks-repository'
import { TodaySchedule } from './components/TodaySchedule'
import { TodayClinicSnapshot } from './components/TodayClinicSnapshot'
import {
  firebaseOverviewRepository,
  type OverviewRepository,
} from './overview-repository'
import { observedScheduleRange } from './today-clinic-model'
import {
  useOverviewMetrics,
  type OverviewMetricStatus,
} from './use-overview-metrics'
import { useOverviewAttention } from './use-overview-operations'
import { useTodaySchedule } from './use-today-schedule'
import styles from './OverviewPage.module.css'

function statusPresentation(status: OverviewMetricStatus): {
  label: string
  tone: StatusTone
} {
  switch (status) {
    case 'ready':
      return { label: 'Live', tone: 'success' }
    case 'warming':
    case 'loading':
      return { label: 'Warming up', tone: 'warning' }
    case 'stale':
      return { label: 'Stale', tone: 'danger' }
    case 'forbidden':
      return { label: 'Restricted', tone: 'neutral' }
    default:
      return { label: 'Offline', tone: 'danger' }
  }
}

function clinicDayLabel(startAtMs: number, timeZone: string) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(startAtMs)
}

function clinicTimeLabel(timestamp: number, timeZone: string) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp)
}

function useLiveNow() {
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 30_000)
    return () => window.clearInterval(interval)
  }, [])
  return nowMs
}

export function OverviewContent({
  tenantId,
  repository,
  readSideRepository,
  renderAppointmentDetail,
  bookingChecksRepository = null,
}: {
  tenantId: string
  repository: OverviewRepository
  readSideRepository?: ReadSideRepository
  bookingChecksRepository?: BookingChecksRepository | null
  renderAppointmentDetail?: (
    appointment: AppointmentItem,
    onClose: () => void
  ) => ReactNode
}) {
  const liveMetrics = useOverviewMetrics(repository, tenantId)
  const schedule = useTodaySchedule(repository, tenantId)
  const attention = useOverviewAttention(repository, tenantId)
  const nowMs = useLiveNow()
  const readSide = useReadSideStatus(readSideRepository, tenantId)
  const status =
    readSidePresentation(readSide.data?.state ?? null) ??
    (readSideRepository &&
    !readSide.data &&
    liveMetrics.status === 'unavailable'
      ? {
          label: readSide.isError
            ? 'Read status unavailable'
            : 'Checking read status',
          tone: 'warning' as const,
        }
      : statusPresentation(liveMetrics.status))
  const dayLabel = schedule.bounds
    ? clinicDayLabel(schedule.bounds.startAtMs, schedule.bounds.timeZone)
    : null
  const observedRange = observedScheduleRange(schedule.items)
  const rangeLabel =
    observedRange && schedule.bounds
      ? `${clinicTimeLabel(observedRange.startAtMs, schedule.bounds.timeZone)}–${clinicTimeLabel(observedRange.endAtMs, schedule.bounds.timeZone)}`
      : null

  return (
    <section className={styles.page}>
      <div className={styles.operationalContext}>
        <div className={styles.contextCopy}>
          <strong className={styles.contextDay}>
            {dayLabel ?? 'Preparing today'}
          </strong>
          {rangeLabel || schedule.bounds ? (
            <p className={styles.contextMeta}>
              <Clock3 size={14} aria-hidden="true" />
              {rangeLabel ? <span>{rangeLabel}</span> : null}
              {schedule.bounds ? <span>{schedule.bounds.timeZone}</span> : null}
            </p>
          ) : null}
          <p className={styles.contextSource}>
            <Info size={14} aria-hidden="true" />
            <span>
              Appointment counts and statuses come from the clinic’s practice
              management system (PMS), not from actions taken by Flio.
            </span>
          </p>
        </div>
        <StatusBadge tone={status.tone} pulse={status.label === 'Live'}>
          {status.label}
        </StatusBadge>
      </div>

      <TodayClinicSnapshot
        schedule={schedule}
        attentionCount={attention.data?.outstandingCount ?? null}
        attentionLoading={attention.isPending}
        nowMs={nowMs}
      />

      <div className={styles.operationsGrid}>
        <TodaySchedule
          schedule={schedule}
          nowMs={nowMs}
          tenantId={tenantId}
          renderAppointmentDetail={renderAppointmentDetail}
        />
        <NeedsAttention
          tenantId={tenantId}
          repository={repository}
          bookingChecksRepository={bookingChecksRepository}
        />
      </div>
    </section>
  )
}

export function OverviewPage({
  repository = firebaseOverviewRepository,
}: {
  repository?: OverviewRepository
}) {
  const tenant = useTenant()
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const readSideRepository = useMemo(
    () => createReadSideRepository({ getUser: () => user }),
    [user]
  )
  const bookingChecksRepository = useMemo(
    () => createBookingChecksRepository({ getUser: () => user }),
    [user]
  )
  if (!tenant.selectedClinic) {
    return <Spinner label="Loading clinic overview" />
  }

  return (
    <OverviewContent
      key={tenant.selectedClinic.id}
      tenantId={tenant.selectedClinic.id}
      repository={repository}
      readSideRepository={readSideRepository}
      bookingChecksRepository={bookingChecksRepository}
      renderAppointmentDetail={(appointment, onClose) => (
        <ConnectedAppointmentDetailModal
          key={appointment.appointmentId}
          appointment={appointment}
          tenantId={tenant.selectedClinic!.id}
          nowMs={Date.now()}
          onClose={onClose}
        />
      )}
    />
  )
}
